import { env } from "config";

/**
 * Instance region. The codebase is shared between the global (jmix.io) and the Russian
 * (jmix.ru) instances; region-specific behavior (language, trackers, legal links) is
 * switched at runtime via the REACT_APP_REGION container variable.
 *
 * Anything unrecognized falls back to GLOBAL, so existing deployments keep their behavior.
 */
export enum Region {
  GLOBAL = "global",
  RU = "ru",
}

export function getRegion(): Region {
  const value = (env.REACT_APP_REGION || "").toLowerCase();
  return value === Region.RU ? Region.RU : Region.GLOBAL;
}

export function isRu(): boolean {
  return getRegion() === Region.RU;
}

/** Locale requested from Keycloak login pages (ui_locales); undefined keeps server defaults. */
export function getKeycloakLocale(): string | undefined {
  return isRu() ? "ru" : undefined;
}

const DOCS_HOSTS: Record<Region, string> = {
  [Region.GLOBAL]: "https://docs.jmix.io",
  [Region.RU]: "https://docs.jmix.ru",
};

/**
 * Documentation link for the current region, e.g. docsUrl("/jmix/account-management.html#consulting").
 */
export function docsUrl(path: string): string {
  return DOCS_HOSTS[getRegion()] + path;
}

const FORUM_HOSTS: Record<Region, string> = {
  [Region.GLOBAL]: "https://forum.jmix.io/",
  [Region.RU]: "https://forum.jmix.ru/",
};

/** Community forum URL for the current region. */
export function forumUrl(): string {
  return FORUM_HOSTS[getRegion()];
}

/**
 * Display name of the main Jmix site ("jmix.io" / "jmix.ru").
 */
export function jmixSiteLabel(): string {
  try {
    return new URL(env.REACT_APP_JMIX_IO).hostname.replace(/^www\./, "");
  } catch {
    return isRu() ? "jmix.ru" : "jmix.io";
  }
}

const russianCountryNames = (() => {
  if (!isRu() || typeof Intl.DisplayNames !== "function") {
    return null;
  }
  try {
    return new Intl.DisplayNames(["ru"], { type: "region" });
  } catch {
    return null;
  }
})();

/**
 * Country name for the current region: the browser-provided Russian name on the RU
 * instance, the backend (English) name everywhere else or when the ISO code is unknown.
 */
export function getCountryDisplayName(code: string, fallbackName: string): string {
  if (!russianCountryNames || !code) {
    return fallbackName;
  }
  try {
    const isoCode = code.toUpperCase();
    const localized = russianCountryNames.of(isoCode);
    return localized && localized !== isoCode ? localized : fallbackName;
  } catch {
    return fallbackName;
  }
}

